<template>
  <v-dialog v-model="showDialog" max-width="800px" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("addAccountDialog.title") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          :aria-label="t('common.close')"
          variant="text"
          size="small"
          :disabled="loading"
          @click="showDialog = false"
        />
      </v-card-title>
      <v-card-text>
        <v-stepper width="400" class="mx-auto" flat :model-value="currentStep">
          <v-stepper-header>
            <v-stepper-item
              color="primary"
              :title="t('addAccountDialog.accountType')"
              value="1"
              editable
              :subtitle="selectedOption ? t(`addAccountDialog.${selectedOption}`) : ''"
              :complete="currentStep > 0"
              @click="currentStep = 0"
            ></v-stepper-item>

            <v-divider></v-divider>

            <v-stepper-item
              :title="t('addAccountDialog.information')"
              value="2"
              color="primary"
              :complete="currentStep > 1"
              :active="currentStep === 1"
            ></v-stepper-item>
          </v-stepper-header>
        </v-stepper>

        <template v-if="currentStep === 0">
          <v-row class="mt-2">
            <v-col v-for="option in accountOptions" :key="option.type" cols="12">
              <v-card
                flat
                border
                link
                style="cursor: pointer; transition-property: all"
                class="px-5 pt-1 pb-5 hover-card"
                :data-testid="option.dataTestid"
                @click="selectedOption = option.type"
              >
                <div>
                  <div class="flex items-center">
                    <v-checkbox
                      v-model="selectedOption"
                      color="primary"
                      hide-details
                      :value="option.type"
                    ></v-checkbox>
                    <div>{{ option.title }}</div>
                  </div>
                  <div class="text-sm text-medium-emphasis ml-3">
                    {{ option.description }}
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <v-form
          v-if="currentStep === 1 && selectedOption === 'attachAccount'"
          id="attach-account-form"
          ref="form"
          :disabled="loading"
          @submit.prevent="onAttach"
        >
          <v-text-field
            v-model.trim="siren"
            hide-details="auto"
            inputmode="numeric"
            :rules="[
              required,
              (value: string) => /^\d{9}$/.test(value) || t('addAccountDialog.sirenFormat'),
            ]"
            data-testid="add-account-dialog-siren-input"
          >
            <template #label>
              {{ t("addAccountDialog.siren") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <v-select
            v-model="contractTemplateId"
            class="mt-3"
            :loading="loadingContractTemplates"
            :disabled="loading || loadingContractTemplates"
            :items="contractTemplateItems"
            :rules="[required]"
            hide-details="auto"
          >
            <template #label>
              {{ t("addAccountDialog.contractTemplate") }}
              <span class="text-error">*</span>
            </template>
          </v-select>
          <div class="v-label mt-3">{{ t("collaborators.title") }}</div>
          <v-radio-group
            v-model="assignedSupervisorsMode"
            class="mt-2"
            hide-details
            color="primary"
          >
            <v-radio :label="t('collaborators.modes.all')" value="all"></v-radio>
            <v-radio :label="t('collaborators.modes.selected')" value="selected"></v-radio>
            <v-radio :label="t('collaborators.modes.none')" value="none"></v-radio>
          </v-radio-group>
          <v-autocomplete
            v-if="assignedSupervisorsMode === 'selected'"
            v-model="selectedSupervisorIds"
            class="mt-3"
            :items="supervisorOrgUsers"
            item-title="email"
            item-value="uid"
            :label="t('collaborators.title')"
            :rules="[(value: string[]) => !!value.length || t('collaborators.selectAtLeastOne')]"
            multiple
            chips
            closable-chips
            hide-details="auto"
          />
          <div class="v-label mt-3">{{ t("addAccountDialog.transfers") }}</div>
          <v-radio-group v-model="limitTransfers" class="mt-2" hide-details color="primary">
            <v-radio :label="t('addAccountDialog.acceptAllTransfers')" value="unlimited"></v-radio>
            <v-radio :label="t('addAccountDialog.requireValidation')" value="all"></v-radio>
            <v-radio
              :label="t('addAccountDialog.requireValidationAbove')"
              value="threshold"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="limitTransfers === 'threshold'"
            v-model.number="transferAmountThreshold"
            type="number"
            :rules="[required, positiveNumber]"
            min="0"
            class="mt-2"
            prepend-inner-icon="mdi-currency-eur"
            hide-details="auto"
          >
            <template #label>
              {{ t("addAccountDialog.transferValidationThreshold") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <div class="v-label mt-3">{{ t("notifications.sendEmailWhen") }}</div>
          <div class="grid grid-cols-2 gap-x-3 mt-2">
            <v-text-field
              v-model.number="notifications.balance.max"
              clearable
              type="number"
              :label="t('notifications.balanceAbove')"
              append-inner-icon="mdi-currency-eur"
            />
            <v-text-field
              v-model.number="notifications.balance.min"
              clearable
              type="number"
              :label="t('notifications.balanceBelow')"
              append-inner-icon="mdi-currency-eur"
            />
            <v-text-field
              v-model.number="notifications.transaction.max"
              clearable
              type="number"
              :label="t('notifications.transactionAbove')"
              append-inner-icon="mdi-currency-eur"
            />
            <v-select
              :model-value="notifications.inactivity.enabled ? notifications.inactivity.days : 0"
              :items="inactivityItems"
              :label="t('notifications.inactivity')"
              @update:model-value="
                (days: number) =>
                  (notifications.inactivity = days
                    ? { enabled: true, days }
                    : { enabled: false, days: 30 })
              "
            />
          </div>
          <div class="text-sm mt-2">{{ t("addAccountDialog.attachAccountInfo") }}</div>
        </v-form>

        <v-form
          v-else-if="currentStep === 1"
          id="add-account-form"
          ref="form"
          :disabled="loading"
          @submit.prevent="onSave"
        >
          <div class="font-bold mb-2">{{ t("addAccountDialog.details") }}</div>
          <v-text-field
            v-model.trim="accountName"
            hide-details="auto"
            :rules="[required]"
            data-testid="add-account-dialog-name-input"
          >
            <template #label>
              {{ t("addAccountDialog.accountName") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <template v-if="selectedOption === 'proAccount'">
            <v-select
              v-model="proAccountPrice"
              class="mt-5"
              :loading="loadingSupervisorOrg"
              :disabled="loading || !!loadingSupervisorOrg"
              :rules="[required]"
              :items="proAccountPriceOptionsItems"
              prepend-inner-icon="mdi-currency-eur"
              hide-details="auto"
            >
              <template #label>
                {{ t("addAccountDialog.proAccountPrice") }}
                <span class="text-error">*</span>
              </template>
            </v-select>
            <ProAccountPricingTiersHint :show="hasMultipleProAccountPrices" />
            <v-select
              v-if="showContractTemplateSelect"
              v-model="contractTemplateId"
              class="mt-3"
              :loading="loadingContractTemplates"
              :disabled="loading || loadingContractTemplates"
              :items="contractTemplateItems"
              :rules="[required]"
              hide-details="auto"
            >
              <template #label>
                {{ t("addAccountDialog.contractTemplate") }}
                <span class="text-error">*</span>
              </template>
            </v-select>
            <div class="v-label mt-3">{{ t("addAccountDialog.transfers") }}</div>
            <v-radio-group v-model="limitTransfers" class="mt-2" hide-details color="primary">
              <v-radio
                :label="t('addAccountDialog.acceptAllTransfers')"
                value="unlimited"
              ></v-radio>
              <v-radio :label="t('addAccountDialog.requireValidation')" value="all"></v-radio>
              <v-radio
                :label="t('addAccountDialog.requireValidationAbove')"
                value="threshold"
              ></v-radio>
            </v-radio-group>
            <template v-if="limitTransfers === 'threshold'">
              <v-text-field
                v-model.number="transferAmountThreshold"
                type="number"
                :rules="[required, positiveNumber]"
                min="0"
                class="mt-2"
                prepend-inner-icon="mdi-currency-eur"
                hide-details="auto"
              >
                <template #label>
                  {{ t("addAccountDialog.transferValidationThreshold") }}
                  <span class="text-error">*</span>
                </template>
              </v-text-field>
            </template>
            <div class="v-label mt-3">{{ t("addAccountDialog.cards") }}</div>
            <v-switch
              v-model="useCard"
              color="primary"
              :label="t('addAccountDialog.allowCards')"
              hide-details="auto"
            ></v-switch>
          </template>
          <div class="font-bold mb-2 mt-3">{{ t("addAccountDialog.accountHolder") }}</div>

          <v-select
            v-model="operatorLanguage"
            class="mt-4"
            :items="languageOptions"
            :label="t('account.operatorLanguage')"
            :rules="[required]"
          >
          </v-select>
          <v-text-field
            v-model.trim="contactEmail"
            :rules="[required, validEmail]"
            data-testid="add-account-dialog-email-input"
          >
            <template #label>
              {{ t("addAccountDialog.contactEmail") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <v-text-field
            v-model.trim="contactPhone"
            :rules="[required]"
            data-testid="add-account-dialog-phone-input"
          >
            <template #label>
              {{ t("addAccountDialog.contactPhone") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <div class="text-sm">
            {{
              selectedOption === "openBanking"
                ? t("addAccountDialog.openBankingContactInfo")
                : t("addAccountDialog.proAccountContactInfo")
            }}
          </div>
          <AdminOnlyCard v-if="selectedOption === 'openBanking'" class="mt-5">
            <v-radio-group
              v-model="openBankingProvider"
              :label="t('addAccountDialog.provider')"
              color="primary"
              inline
              class="mt-2 -ml-4"
              hide-details
            >
              <v-radio :label="t('addAccountDialog.providerDefault')" :value="null"></v-radio>
              <v-radio
                v-for="p in openBankingProviders"
                :key="p"
                :label="p"
                :value="p"
                :data-testid="`add-account-dialog-provider-${p}`"
              ></v-radio>
            </v-radio-group>
          </AdminOnlyCard>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn :disabled="loading" @click="showDialog = false">{{ t("common.close") }}</v-btn>
        <v-spacer />
        <v-btn
          v-if="currentStep === 0"
          variant="flat"
          color="primary"
          :disabled="!selectedOption"
          data-testid="add-account-dialog-next-button"
          @click="currentStep = 1"
          >{{ t("common.next") }}</v-btn
        >
        <v-btn
          v-else-if="selectedOption === 'attachAccount'"
          variant="flat"
          type="submit"
          form="attach-account-form"
          :loading="loading"
          :disabled="loading || loadingContractTemplates"
          color="success"
          data-testid="add-account-dialog-attach-button"
          >{{ t("addAccountDialog.attachAccountSubmit") }}</v-btn
        >
        <v-btn
          v-else
          variant="flat"
          type="submit"
          form="add-account-form"
          :loading="loading"
          :disabled="loading || isContractTemplatePending"
          color="success"
          data-testid="add-account-dialog-submit-button"
          >{{ t("common.add") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import AdminOnlyCard from "@/components/AdminOnlyCard.vue";
import ProAccountPricingTiersHint from "@/components/ProAccountPricingTiersHint.vue";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { useSupervisorOrg } from "@/composables/useSupervisorOrg";
import { call, db } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import type { AssignedSupervisors, OpenBankingAccountType } from "@aircap/common/account";
import { callables } from "@aircap/common/callables";
import type { Locale } from "@aircap/common/models";
import { locales } from "@aircap/common/models";
import type { ContractTemplate, SupervisorOrgUser } from "@aircap/common/supervisorOrg";
import {
  contractTemplatesFullPath,
  supervisorOrgUsersFullPath,
} from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import { collection, orderBy, query, where } from "firebase/firestore";
import { FunctionsError } from "firebase/functions";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import type { VForm } from "vuetify/components";
import type { z } from "zod";

const { t } = useI18n();
const router = useRouter();
const toast = useToast();
const { currentLanguage } = useLanguage();
const { required, validEmail, positiveNumber } = useRules();

const accountName = ref("");
const contactEmail = ref("");
const contactPhone = ref("");
const loading = ref(false);
const user = useUserStore();
const limitTransfers = ref<"unlimited" | "all" | "threshold" | null>("unlimited");
const transferAmountThreshold = ref<number | null>(0);
const form = ref<VForm>();
const showDialog = defineModel<boolean>();
const siren = ref("");
const currentStep = ref(0);
const openBankingProviders = ["bridge", "powens", "gcl"] as const;
const openBankingProvider = ref<(typeof openBankingProviders)[number] | null>(null);
const operatorLanguage = ref<Locale>(currentLanguage.value.code);
const languageOptions = computed(() =>
  locales.map((locale) => ({
    title: t(`locales.${locale}`),
    value: locale,
  })),
);
const useCard = ref(false);
const proAccountPrice = ref<number | null>(null);
const contractTemplateId = ref<string | undefined>(undefined);
const selectedSupervisorIds = ref<string[]>([]);
const assignedSupervisorsMode = ref<AssignedSupervisors["mode"]>("none");
const notifications = ref<
  z.infer<typeof callables.inviteTreezorAccountBySiren.params>["notifications"]
>({
  balance: { max: null, min: null },
  transaction: { max: null },
  inactivity: { enabled: false, days: 30 },
});

const { supervisorOrg, loadingSupervisorOrg } = useSupervisorOrg();

const contractTemplatesRef = computed(() =>
  user.supervisorOrgId
    ? query(
        collection<ContractTemplate>(db, contractTemplatesFullPath(user.supervisorOrgId)),
        orderBy<ContractTemplate>("createdAt", "asc"),
      )
    : null,
);
const { data: contractTemplates, pending: loadingContractTemplates } =
  useCollection<ContractTemplate>(contractTemplatesRef);
const contractTemplateItems = computed(() =>
  contractTemplates.value.map((template) => ({ title: template.name, value: template.id })),
);

watch(supervisorOrg, () => {
  if (!proAccountPrice.value)
    proAccountPrice.value = supervisorOrg.value?.proAccountPriceOptions?.[0] ?? null;
});

watch(
  contractTemplates,
  () => {
    const templates = contractTemplates.value;

    if (templates.length === 1) {
      contractTemplateId.value = templates[0]?.id;
      return;
    }

    if (
      contractTemplateId.value &&
      !templates.some((template) => template.id === contractTemplateId.value)
    ) {
      contractTemplateId.value = undefined;
    }
  },
  { deep: true },
);

const inactivityItems = computed(() => [
  { title: t("notifications.inactivityNone"), value: 0 },
  ...[30, 90, 180].map((days) => ({
    title: t("notifications.inactivityDays", { days }),
    value: days,
  })),
]);

const supervisorOrgUsersRef = computed(() =>
  user.supervisorOrgId
    ? query(
        collection<SupervisorOrgUser>(db, supervisorOrgUsersFullPath(user.supervisorOrgId)),
        where<SupervisorOrgUser>("role", "in", ["supervisor", "nonApprovingSupervisor"]),
      )
    : null,
);
const { data: supervisorOrgUsers } = useCollection<SupervisorOrgUser>(supervisorOrgUsersRef);

const proAccountPriceOptionsItems = computed(
  () => supervisorOrg.value?.proAccountPriceOptions ?? [],
);
const hasMultipleProAccountPrices = computed(() => proAccountPriceOptionsItems.value.length > 1);
const showContractTemplateSelect = computed(() => {
  if (supervisorOrg.value?.contractType !== "signatureApi") return false;
  if (loadingContractTemplates.value) return true;
  return !(contractTemplates.value.length === 1 && contractTemplateId.value !== undefined);
});

const accountOptions = computed(() => {
  return [
    {
      type: "proAccount",
      title: t("addAccountDialog.proAccountTitle"),
      description: t("addAccountDialog.proAccountDescription"),
      dataTestid: "add-account-dialog-option-pro-account",
    },
    {
      type: "openBanking",
      title: t("addAccountDialog.openBankingTitle"),
      description: t("addAccountDialog.openBankingDescription"),
      dataTestid: "add-account-dialog-option-open-banking",
    },
    ...(supervisorOrg.value?.contractType === "signatureApi"
      ? [
          {
            type: "attachAccount",
            title: t("addAccountDialog.attachAccountTitle"),
            description: t("addAccountDialog.attachAccountDescription"),
            dataTestid: "add-account-dialog-option-attach-account",
          } as const,
        ]
      : []),
  ] as const;
});

const selectedOption = ref<"proAccount" | "openBanking" | "attachAccount" | null>(null);

const onAttach = async () => {
  if (!user.supervisorOrgId) throw new Error("Supervisor org is not defined");
  if (!form.value) return;

  try {
    const { valid } = await form.value.validate();
    if (!valid || !contractTemplateId.value) return;

    loading.value = true;
    let threshold: number | null = null;
    if (limitTransfers.value === "all") threshold = 0;
    if (limitTransfers.value === "threshold") threshold = transferAmountThreshold.value;
    let assignedSupervisors: AssignedSupervisors = {
      mode: "selected",
      uids: selectedSupervisorIds.value,
    };
    if (assignedSupervisorsMode.value !== "selected")
      assignedSupervisors = { mode: assignedSupervisorsMode.value };
    await call("inviteTreezorAccountBySiren")({
      supervisorOrgId: user.supervisorOrgId,
      siren: siren.value,
      contractTemplateId: contractTemplateId.value,
      assignedSupervisors,
      transferAmountThreshold: threshold,
      notifications: notifications.value,
    });
    toast.showSuccessToast(t("addAccountDialog.attachAccountSuccess"));
    showDialog.value = false;
  } catch (cause) {
    const errorDetails = callables.inviteTreezorAccountBySiren.errorDetails.safeParse(
      cause instanceof FunctionsError ? cause.details : undefined,
    );
    if (errorDetails.success) {
      toast.showErrorToast(t(`addAccountDialog.attachAccountErrors.${errorDetails.data.reason}`));
      return;
    }
    const error = new Error("Failed to invite project by SIREN", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast(t("addAccountDialog.attachAccountError"));
  } finally {
    loading.value = false;
  }
};

const isContractTemplatePending = computed(
  () =>
    selectedOption.value === "proAccount" &&
    (loadingSupervisorOrg.value ||
      (supervisorOrg.value?.contractType === "signatureApi" && loadingContractTemplates.value)),
);

const onSave = async () => {
  if (!user.supervisorOrgId) throw new Error("Supervisor org is not defined");

  if (!form.value || !selectedOption.value) return;

  try {
    const { valid } = await form.value.validate();
    if (!valid) return;
    if (loadingSupervisorOrg.value || loadingContractTemplates.value) return;

    if (
      selectedOption.value === "proAccount" &&
      supervisorOrg.value?.contractType === "signatureApi" &&
      !contractTemplates.value.some((template) => template.id === contractTemplateId.value)
    ) {
      toast.showErrorToast(t("account.settings.missingContractTemplateId"));
      return;
    }

    let accountType: "swan" | OpenBankingAccountType = "swan";
    if (selectedOption.value === "openBanking")
      accountType =
        openBankingProvider.value || supervisorOrg.value?.defaultOpenBankingProvider || "powens";

    console.log("Saving new project...");
    loading.value = true;
    const accountId = await call("createAccount")({
      contactEmail: contactEmail.value.toLowerCase(),
      contactPhone: contactPhone.value,
      accountName: accountName.value,
      accountType,
      transferAmountThreshold:
        selectedOption.value === "proAccount"
          ? limitTransfers.value === "all"
            ? 0
            : limitTransfers.value === "threshold"
              ? transferAmountThreshold.value
              : null
          : null,
      supervisorOrgId: user.supervisorOrgId,
      operatorLanguage: operatorLanguage.value,
      useCard: useCard.value,
      proAccountPrice:
        selectedOption.value === "proAccount" ? (proAccountPrice.value ?? undefined) : undefined,
      contractTemplateId:
        selectedOption.value === "proAccount" ? contractTemplateId.value : undefined,
    });

    await router.push(`/account/${accountId}`);
    showDialog.value = false;
  } catch (cause) {
    const error = new Error("Failed to create project", { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast(t("addAccountDialog.error"));
  } finally {
    loading.value = false;
  }
};

watch(selectedOption, () => {
  openBankingProvider.value = null;
});

watch(showDialog, () => {
  form.value?.reset();
  form.value?.resetValidation();
  currentStep.value = 0;
  selectedOption.value = null;
  limitTransfers.value = "all";
  transferAmountThreshold.value = 0;
  openBankingProvider.value = null;
  operatorLanguage.value = currentLanguage.value.code;
  contactPhone.value = "";
  contactEmail.value = "";
  accountName.value = "";
  siren.value = "";
  selectedSupervisorIds.value = user.isKind("admin") || !user.uid ? [] : [user.uid];
  assignedSupervisorsMode.value = selectedSupervisorIds.value.length ? "selected" : "none";
  notifications.value = {
    balance: { max: null, min: null },
    transaction: { max: null },
    inactivity: { enabled: false, days: 30 },
  };
  useCard.value = false;
  proAccountPrice.value = supervisorOrg.value?.proAccountPriceOptions?.[0] ?? null;
  contractTemplateId.value =
    contractTemplates.value.length === 1 ? contractTemplates.value[0]?.id : undefined;
});
</script>

<style scoped>
.hover-card:hover {
  border-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
